import { useContext } from 'react'

import {
  Box, Button, Form, ModalContext, SidebarModal, ToggleButton,
} from '@domain-group/suit-theme'

import useCampaignQuoteItems from '@hooks/useCampaignQuoteItems'

import NoticeBox from '@components/NoticeBox'

import useCampaignQuoteItem from '@hooks/useCampaignQuoteItem'
import handleCallbackAction from '@functions/handleCallbackAction'

import useCampaignQuote from '@hooks/useCampaignQuote'
import useCampaign from '@hooks/useCampaign'

import useCampaignQuoteItemDatesModal from './hooks/useCampaignQuoteItemDatesModal'
import QuoteSection from './components/QuoteSection'

const MODAL_KEY = 'AssignCampaignQuoteItemDatesModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const AssignCampaignQuoteItemDatesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { campaignQuote: initCampaignQuote, isApprovalFlow } = modalPayload

  const campaignQuotePayload = useCampaignQuote(initCampaignQuote)
  const {
    callbacks: {
      processQuoteApproval: processFn,
    },
  } = campaignQuotePayload

  const {
    callbacks: {
      editCampaignDates,
    },
  } = useCampaign({ id: initCampaignQuote?.campaign_id })

  const {
    filteredCampaignQuoteItems,
  } = useCampaignQuoteItems({ filters: { campaign_quote_id: initCampaignQuote?.id } })

  const campaignQuoteItemsWithoutPublications = filteredCampaignQuoteItems.filter(item => !item.publication_id)
  const hasCampaignQuoteItemsWithoutPublications = !!campaignQuoteItemsWithoutPublications.length

  const {
    callbacks: {
      bulkAssignAttributes: bulkAssignFn,
    },
    updating,
  } = useCampaignQuoteItem()

  const campaignQuoteItemDatesModalPayload = useCampaignQuoteItemDatesModal({
    campaignQuote: initCampaignQuote,
    filteredCampaignQuoteItems: campaignQuoteItemsWithoutPublications,
  })
  const {
    callbacks: {
      handleUpdateAllItemsRequiredByToday,
    },
    filteredCampaignQuoteSections,
    state,
    setState,
    sortedSectionItemIds,
  } = campaignQuoteItemDatesModalPayload

  const { allItemsRequiredByToday } = state

  const assignBulkRequiredByDatesPayload = {
    callbacks: {
      action: bulkAssignFn,
      afterAction: () => {
        if (isApprovalFlow){
          return editCampaignDates({ callbacks: { successFn: (startDate?: string) => processFn({ startDate }) } })
        }

        return closeModal()
      },
    },
    entityParams: Object.values(state.campaignQuoteItems),
    toastText: 'Campaign Quote Item Required By Dates Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Required Dates" />

      <SidebarModal.Content>
        <Form>
          <Box justifyContent="space-between" alignItems="center">
            <NoticeBox
              actionContent={(
                <ToggleButton
                  boxProps={{
                    height: 'fit-content',
                    justifyContent: 'center',
                    marginLeft: 'medium',
                    width: '90px',
                  }}
                  onClick={() => handleUpdateAllItemsRequiredByToday()}
                  toggleOn={allItemsRequiredByToday}
                  toggleOffText="No"
                  toggleOnText="Yes"
                />
            )}
              boxProps={{
                marginBottom: 'none',
              }}
              variant="white"
            >
              <NoticeBox.Title>
                All items required ASAP
              </NoticeBox.Title>

              <NoticeBox.Paragraph boxProps={{ marginY: 'small' }}>
                This will set &#39;Required By&#39; dates for all items to today&#39;s date.
              </NoticeBox.Paragraph>
            </NoticeBox>
          </Box>

          <Box flexDirection="column">
            {filteredCampaignQuoteSections.map((campaignQuoteSection) => {
              const { id } = campaignQuoteSection
              // @ts-expect-error TS7053
              const sortedItemIds = sortedSectionItemIds[`Section-${id!}`] || []

              return (
                <QuoteSection
                  key={`Section-${id}`}
                  campaignQuoteSection={campaignQuoteSection}
                  filteredCampaignQuoteItems={campaignQuoteItemsWithoutPublications}
                  hasCampaignQuoteItemsWithoutPublications={hasCampaignQuoteItemsWithoutPublications}
                  sortedCampaignQuoteItemIds={sortedItemIds}
                  state={state}
                  setState={setState}
                />
              )
            })}
          </Box>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!initCampaignQuote}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => handleCallbackAction(assignBulkRequiredByDatesPayload)}
          size="large"
        >
          Apply Changes
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <AssignCampaignQuoteItemDatesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
